import { useEffect, useState } from "react";
import { generatePlan, reschedulePlan, listInterventions, listContracts, listClients, confirmIntervention, unconfirmIntervention, updateIntervention, createIntervention, deleteIntervention, duplicateIntervention, moveIntervention, clearReplanningFlag } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Route, Play, MapPin, CalendarClock, RefreshCw, CheckCircle2, Mail, Plus, Trash2, Copy, GripVertical, AlertTriangle, StickyNote, FileText } from "lucide-react";
import { toast } from "sonner";

// Inline note editor: click icon to open popover with textarea
function NoteCell({ iv, onSaved }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(iv.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(iv.notes || ""); }, [iv.notes]);
  const save = async () => {
    const v = (value || "").trim();
    if (v === (iv.notes || "")) { setOpen(false); return; }
    setSaving(true);
    try {
      await updateIntervention(iv.id, { notes: v });
      toast.success(v ? "Note aggiornate" : "Note eliminate");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };
  const hasNotes = !!(iv.notes && iv.notes.trim());
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`notes-btn-${iv.id}`}
          title={hasNotes ? iv.notes : "Aggiungi note"}
          className={`shrink-0 rounded p-0.5 transition-colors ${hasNotes ? "text-amber-600 hover:bg-amber-50" : "text-slate-300 hover:text-slate-500 hover:bg-slate-50"}`}
        >
          {hasNotes ? <StickyNote className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 bg-white" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-700 flex items-center gap-1">
            <StickyNote className="w-3.5 h-3.5 text-amber-600" /> Note intervento
          </div>
          <Textarea
            data-testid={`notes-textarea-${iv.id}`}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Dettagli, riferimenti, materiali da portare..."
            rows={4}
            className="text-sm"
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setValue(iv.notes || ""); setOpen(false); }} className="h-7 text-xs">
              Annulla
            </Button>
            <Button
              data-testid={`notes-save-${iv.id}`}
              size="sm"
              onClick={save}
              disabled={saving}
              className="h-7 text-xs bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : null}
              Salva
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Planning() {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [ivs, setIvs] = useState([]);
  const [startFrom, setStartFrom] = useState("");
  const [endMode, setEndMode] = useState("none"); // "none" | "date" | "days"
  const [endDate, setEndDate] = useState("");
  const [horizonDays, setHorizonDays] = useState(30);
  const [workdaysOnly, setWorkdaysOnly] = useState(true);
  const [maxBlock, setMaxBlock] = useState(1);
  const [clientFilter, setClientFilter] = useState("all"); // "all" or client_id
  const [radiusEnabled, setRadiusEnabled] = useState(false);
  const [radiusKm, setRadiusKm] = useState(50);
  const [running, setRunning] = useState(false);
  const [lastSkipped, setLastSkipped] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ contract_id: "", date: "", slot: "full", start_time: "", end_time: "", notes: "" });
  const [saving, setSaving] = useState(false);
  // Filters per "Interventi programmati": default nasconde il passato
  const [showPast, setShowPast] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const openAdd = (contract_id) => {
    setAddForm({ contract_id, date: new Date().toISOString().slice(0, 10), slot: "full", start_time: "", end_time: "", notes: "" });
    setAddOpen(true);
  };
  const submitAdd = async () => {
    if (!addForm.date) { toast.error("Data obbligatoria"); return; }
    setSaving(true);
    try {
      await createIntervention({
        contract_id: addForm.contract_id,
        date: addForm.date,
        slot: addForm.slot,
        start_time: addForm.start_time || null,
        end_time: addForm.end_time || null,
        notes: addForm.notes || null,
      });
      toast.success("Appuntamento aggiunto");
      setAddOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiunta");
    } finally { setSaving(false); }
  };
  const doDelete = async (iv_id) => {
    if (!window.confirm("Cancellare questo intervento? L'operazione non è reversibile.")) return;
    try {
      await deleteIntervention(iv_id);
      toast.success("Intervento cancellato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const doDuplicate = async (iv_id) => {
    try {
      const r = await duplicateIntervention(iv_id);
      toast.success(`Duplicato al ${r.date}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore duplicazione"); }
  };

  // ---------------- DRAG & DROP -----------------
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const onDragStart = (e, id) => {
    setDragId(id);
    try { e.dataTransfer.setData("text/plain", id); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDragEnd = () => { setDragId(null); setDragOverId(null); };
  const onDrop = async (e, tgtIv) => {
    e.preventDefault();
    const srcId = dragId || e.dataTransfer.getData("text/plain");
    setDragId(null); setDragOverId(null);
    if (!srcId || srcId === tgtIv.id) return;
    try {
      const r = await moveIntervention(srcId, { target_iv_id: tgtIv.id });
      if (r.action === "swapped") {
        toast.success("Interventi scambiati (mattina/pomeriggio)");
      } else if (r.action === "moved") {
        const n = (r.displaced || []).length;
        if (n > 0) toast.warning(`Spostato al ${tgtIv.date} · ${n} intervento/i spostato/i in giorni successivi (rosso: DA PIANIFICARE)`);
        else toast.success(`Spostato al ${tgtIv.date}`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore spostamento"); }
  };
  const doClearReplanning = async (iv_id) => {
    try { await clearReplanningFlag(iv_id); toast.success("Conferma nuova data"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const load = () => Promise.all([listContracts(), listClients(), listInterventions()])
    .then(([cs, cls, iv]) => { setContracts(cs); setClients(cls); setIvs(iv); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const clientFor = (id) => clients.find((c) => c.id === id);
  const contractFor = (id) => contracts.find((c) => c.id === id);

  const remainingByContract = contracts
    .filter((c) => c.status === "active" || c.status === "planned")
    .map((c) => {
      const done = ivs.filter((iv) => iv.contract_id === c.id).length;
      return { c, done, rem: c.total_days - done };
    })
    .filter((r) => r.rem > 0);

  const buildPlanPayload = () => ({
    start_from: startFrom || null,
    end_date: endMode === "date" ? (endDate || null) : null,
    horizon_days: endMode === "days" ? Math.max(1, Number(horizonDays) || 0) : null,
    workdays_only: workdaysOnly,
    max_days_per_client_block: Number(maxBlock),
    client_id: clientFilter === "all" ? null : clientFilter,
    radius_km: radiusEnabled ? Math.max(1, Number(radiusKm) || 0) : null,
  });

  const run = async () => {
    if (endMode === "date" && !endDate) { toast.error("Imposta la data di termine o scegli un'altra modalità"); return; }
    if (endMode === "days" && (!horizonDays || Number(horizonDays) < 1)) { toast.error("Inserisci un numero di giorni valido"); return; }
    setRunning(true);
    try {
      const res = await generatePlan(buildPlanPayload());
      const skipped = res.skipped || [];
      setLastSkipped(skipped);
      if (skipped.length > 0) {
        toast.warning(`${res.planned} pianificati, ${skipped.length} contratti con giornate NON piazzate (scadenza raggiunta)`);
      } else {
        toast.success(`${res.planned} interventi pianificati`);
      }
      load();
    } catch (_e) { toast.error("Errore generazione piano"); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="planning-generator-section" className="max-w-[96vw] xl:max-w-[92vw] mx-auto">
      <div className="mb-8">
        <div className="text-xs font-medium tracking-wider uppercase text-slate-500">Ottimizzazione</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">Planning</h1>
        <p className="mt-2 text-slate-600">Genera automaticamente gli interventi raggruppati per prossimità geografica, rispettando priorità e scadenze.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border-slate-200 bg-white lg:col-span-1">
          <h2 className="font-bold font-display text-lg text-slate-900 mb-4 flex items-center gap-2">
            <Route className="w-5 h-5 text-blue-700" /> Parametri
          </h2>
          <div className="space-y-4">
            <div>
              <Label>Data di partenza</Label>
              <Input data-testid="planning-start-date" type="date" value={startFrom} onChange={(e) => setStartFrom(e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">Default: domani</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-2">
              <Label className="text-blue-900">Pianificazione per cliente</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger data-testid="planning-client-filter" className="bg-white">
                  <SelectValue placeholder="Tutti i clienti" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value="all">Tutti i clienti (ottimizzazione geografica globale)</SelectItem>
                  {[...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((cl) => (
                    <SelectItem key={cl.id} value={cl.id}>
                      {cl.name}{cl.city ? ` · ${cl.city}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-blue-700">
                {clientFilter === "all"
                  ? "Pianifica tutti i contratti attivi raggruppando per vicinanza geografica"
                  : "Pianifica SOLO i contratti del cliente selezionato (ignora gli altri)"}
              </div>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-emerald-900 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" /> Zona geografica (raggio km)
                </Label>
                <Switch
                  data-testid="planning-radius-toggle"
                  checked={radiusEnabled}
                  onCheckedChange={setRadiusEnabled}
                />
              </div>
              {radiusEnabled && (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="planning-radius-km"
                      type="number"
                      min="1"
                      max="2000"
                      value={radiusKm}
                      onChange={(e) => setRadiusKm(e.target.value)}
                      className="bg-white"
                    />
                    <span className="text-sm text-emerald-900 font-medium">km</span>
                  </div>
                  <div className="text-xs text-emerald-700">
                    Pianifica solo clienti entro <b>{radiusKm || "?"} km</b> dalla tua sede. Imposta le coordinate della sede in Impostazioni → Dati emittente.
                  </div>
                </>
              )}
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-3">
              <Label>Fine pianificazione</Label>
              <Select value={endMode} onValueChange={setEndMode}>
                <SelectTrigger data-testid="planning-end-mode" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">Nessun limite (fino alle scadenze)</SelectItem>
                  <SelectItem value="date">Data di termine</SelectItem>
                  <SelectItem value="days">N° giorni dalla partenza</SelectItem>
                </SelectContent>
              </Select>
              {endMode === "date" && (
                <div>
                  <Input
                    data-testid="planning-end-date"
                    type="date"
                    value={endDate}
                    min={startFrom || undefined}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                  <div className="text-xs text-slate-500 mt-1">Nessun intervento verrà pianificato dopo questa data</div>
                </div>
              )}
              {endMode === "days" && (
                <div>
                  <Input
                    data-testid="planning-horizon-days"
                    type="number"
                    min="1"
                    max="365"
                    value={horizonDays}
                    onChange={(e) => setHorizonDays(e.target.value)}
                  />
                  <div className="text-xs text-slate-500 mt-1">Finestra di {horizonDays || "?"} giorni di calendario dalla data di partenza</div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-900">Solo giorni feriali</div>
                <div className="text-xs text-slate-500">Salta sabato e domenica</div>
              </div>
              <Switch data-testid="planning-workdays-only" checked={workdaysOnly} onCheckedChange={setWorkdaysOnly} />
            </div>
            <div>
              <Label>Max giorni consecutivi per cliente</Label>
              <Input data-testid="planning-max-block" type="number" min="1" max="10" value={maxBlock} onChange={(e) => setMaxBlock(e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">Suddivide contratti lunghi in blocchi</div>
            </div>
            <Button data-testid="planning-run-button" disabled={running || remainingByContract.length === 0} onClick={run} className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              <Play className="w-4 h-4 mr-2" />
              {running ? "Generazione…" : "Genera planning ottimizzato"}
            </Button>
            <Button
              data-testid="planning-reschedule-button"
              variant="outline"
              disabled={running}
              onClick={async () => {
                if (!confirm("Cancellare TUTTI gli interventi già pianificati e riprogrammarli da zero? Gli appuntamenti extra saranno rispettati.")) return;
                if (endMode === "date" && !endDate) { toast.error("Imposta la data di termine o scegli un'altra modalità"); return; }
                if (endMode === "days" && (!horizonDays || Number(horizonDays) < 1)) { toast.error("Inserisci un numero di giorni valido"); return; }
                setRunning(true);
                try {
                  const r = await reschedulePlan(buildPlanPayload());
                  setLastSkipped(r.skipped || []);
                  if ((r.skipped || []).length > 0) {
                    toast.warning(`${r.planned} riprogrammati, ${r.skipped.length} contratti con giornate non piazzate`);
                  } else {
                    toast.success(`Riprogrammati ${r.planned} interventi`);
                  }
                  load();
                } catch (_e) { toast.error("Errore"); }
                finally { setRunning(false); }
              }}
              className="w-full h-11"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
              Riprogramma tutto da capo
            </Button>
            <div className="text-xs text-slate-500 leading-relaxed">
              Usa "Riprogramma tutto" dopo aver inserito nuovi appuntamenti extra dal calendario o aggiunto contratti prioritari.
            </div>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 bg-white lg:col-span-2">
          <h2 className="font-bold font-display text-lg text-slate-900 mb-4 flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-blue-700" /> Contratti da pianificare
          </h2>
          {remainingByContract.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Tutti i contratti sono già pianificati o non ci sono contratti attivi.</div>
          ) : (
            <div className="space-y-2">
              {remainingByContract.map(({ c, done, rem }) => {
                const cli = clientFor(c.client_id);
                return (
                  <div key={c.id} data-testid={`plan-item-${c.id}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{c.title}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {cli?.name} • {cli?.city || cli?.address}
                      </div>
                    </div>
                    <div className="text-right pr-3">
                      <div className="text-sm font-bold text-slate-900">{rem}g</div>
                      <div className="text-[10px] text-slate-500">di {c.total_days} tot</div>
                    </div>
                    <Button data-testid={`add-intervention-${c.id}`} size="sm" variant="outline" onClick={() => openAdd(c.id)} className="h-8 text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {lastSkipped.length > 0 && (
        <Card className="mt-6 p-5 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-5 h-5 text-amber-700" />
            <h2 className="font-bold font-display text-amber-900">Attenzione — giornate non piazzate</h2>
          </div>
          <div className="space-y-2">
            {lastSkipped.map((s, i) => {
              const c = contracts.find((x) => x.id === s.contract_id);
              const cli = c ? clients.find((x) => x.id === c.client_id) : null;
              const clientLabel = cli?.name || "Cliente sconosciuto";
              const reasonLabel = s.reason === "no_start_date"
                ? "Data di inizio mancante nella scheda contratto"
                : s.reason === "deadline_expired"
                ? "Scadenza già superata"
                : "Scadenza raggiunta prima di completare le giornate";
              return (
                <div key={i} data-testid={`skipped-${s.contract_id}`} className="flex items-center justify-between p-2 bg-white border border-amber-200 rounded">
                  <div className="min-w-0 flex-1 pr-3">
                    <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`skipped-client-${s.contract_id}`}>{clientLabel}</div>
                    <div className="text-xs text-slate-700 truncate" data-testid={`skipped-contract-${s.contract_id}`}>Contratto: {c?.title || s.contract_id}</div>
                    <div className="text-xs text-slate-600">{reasonLabel} · Scadenza: <span className="font-mono">{c?.deadline || "n/d"}</span></div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-amber-700">{s.remaining}g</div>
                    <div className="text-[10px] text-slate-500">non piazzati</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-xs text-amber-800">
            Suggerimento: sposta la scadenza in avanti o aumenta il numero di interventi al mese sul contratto.
          </div>
        </Card>
      )}

      {ivs.length > 0 && (
        <Card className="mt-6 p-6 border-slate-200 bg-white">
          {(() => {
            const todayIso = new Date().toISOString().slice(0, 10);
            const visibleIvs = ivs.filter((iv) => {
              if (!showPast && iv.date < todayIso) return false;
              if (dateFrom && iv.date < dateFrom) return false;
              if (dateTo && iv.date > dateTo) return false;
              return true;
            });
            const pastCount = ivs.filter((iv) => iv.date < todayIso).length;
            return (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h2 className="font-bold font-display text-lg text-slate-900">
                    Interventi programmati ({visibleIvs.length}{visibleIvs.length !== ivs.length ? ` di ${ivs.length}` : ""})
                  </h2>
                  <div className="text-[11px] text-slate-500 flex items-center gap-1">
                    <GripVertical className="w-3.5 h-3.5" /> Trascina una riga su un'altra per scambiare orario o spostare a nuova data
                  </div>
                </div>
                <div className="mb-3 flex flex-wrap items-center gap-2 p-2 bg-slate-50 border border-slate-100 rounded-md text-xs">
                  <div className="flex items-center gap-1.5">
                    <Switch data-testid="toggle-show-past" checked={showPast} onCheckedChange={setShowPast} id="show-past" />
                    <Label htmlFor="show-past" className="cursor-pointer">
                      Mostra passati {pastCount > 0 ? <span className="text-slate-400">({pastCount})</span> : null}
                    </Label>
                  </div>
                  <span className="text-slate-300">|</span>
                  <Label htmlFor="date-from" className="text-slate-600">Dal</Label>
                  <Input data-testid="date-from-input" id="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 w-[140px] text-xs" />
                  <Label htmlFor="date-to" className="text-slate-600">al</Label>
                  <Input data-testid="date-to-input" id="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 w-[140px] text-xs" />
                  {(dateFrom || dateTo || showPast) && (
                    <Button data-testid="reset-filters" variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setShowPast(false); }} className="h-7 text-xs">
                      Reset
                    </Button>
                  )}
                </div>
                {visibleIvs.some((iv) => iv.needs_replanning) && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {visibleIvs.filter((iv) => iv.needs_replanning).length} intervento/i DA PIANIFICARE (spostati automaticamente)
                    </div>
                    <div className="text-xs text-red-700 mt-1">Rivedi le date evidenziate in rosso qui sotto e clicca ✓ per confermarle.</div>
                  </div>
                )}
                <div className="max-h-[70vh] overflow-auto rounded-md border border-slate-100">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_0_rgb(226_232_240)]">
                      <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500">
                        <th className="py-2 pr-2 w-6"></th>
                        <th className="py-2 pr-3">Data</th>
                        <th className="py-2 pr-3">Fascia</th>
                        <th className="py-2 pr-3">Orario</th>
                        <th className="py-2 pr-3">Cliente</th>
                        <th className="py-2 pr-3">Contratto</th>
                        <th className="py-2 pr-3">Stato</th>
                        <th className="py-2 pr-3">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...visibleIvs]
                        .sort((a, b) => {
                          if (a.needs_replanning !== b.needs_replanning) return a.needs_replanning ? -1 : 1;
                          if (a.date !== b.date) return a.date < b.date ? -1 : 1;
                          const at = a.start_time || (a.slot === "afternoon" ? "14:30" : (a.slot === "morning" ? "09:00" : "09:00"));
                          const bt = b.start_time || (b.slot === "afternoon" ? "14:30" : (b.slot === "morning" ? "09:00" : "09:00"));
                          return at < bt ? -1 : (at > bt ? 1 : 0);
                        })
                        .slice(0, 100).map((iv) => {
                  const c = contractFor(iv.contract_id);
                  const cli = clientFor(iv.client_id);
                  const slot = iv.slot || "full";
                  const confirmed = iv.status === "confirmed";
                  const doConfirm = async () => {
                    try {
                      const r = await confirmIntervention(iv.id);
                      if (r.warning) toast.warning(r.warning);
                      else toast.success("Confermato ed email inviata al cliente");
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                  };
                  const doUnconfirm = async () => {
                    try { await unconfirmIntervention(iv.id); toast.success("Riportato a pianificato"); load(); }
                    catch (_e) { toast.error("Errore"); }
                  };
                  const doSlot = async (newSlot) => {
                    try {
                      await updateIntervention(iv.id, { slot: newSlot });
                      toast.success("Fascia oraria aggiornata");
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento"); }
                  };
                  const doField = async (field, value) => {
                    try {
                      await updateIntervention(iv.id, { [field]: value });
                      toast.success("Aggiornato");
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                  };
                  const rowCls = [
                    "border-b border-slate-100 transition-colors",
                    iv.needs_replanning ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50",
                    dragOverId === iv.id ? "outline outline-2 outline-blue-400 outline-offset-[-2px]" : "",
                    dragId === iv.id ? "opacity-40" : "",
                  ].join(" ");
                  return (
                    <tr key={iv.id}
                        data-testid={`plan-intervention-${iv.id}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, iv.id)}
                        onDragOver={(e) => onDragOver(e, iv.id)}
                        onDragLeave={() => setDragOverId(null)}
                        onDrop={(e) => onDrop(e, iv)}
                        onDragEnd={onDragEnd}
                        className={rowCls}>
                      <td className="py-2 pr-2 cursor-grab active:cursor-grabbing text-slate-400" title="Trascina per spostare/scambiare">
                        <GripVertical className="w-4 h-4" />
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex items-center gap-1">
                          <Input
                            data-testid={`date-input-${iv.id}`}
                            type="date"
                            defaultValue={iv.date}
                            onBlur={(e) => { if (e.target.value && e.target.value !== iv.date) doField("date", e.target.value); }}
                            className={`h-7 text-[11px] w-[120px] font-mono px-1 ${iv.needs_replanning ? "border-red-400 bg-red-50" : ""}`}
                          />
                          {iv.needs_replanning && (
                            <button
                              data-testid={`clear-replanning-${iv.id}`}
                              onClick={() => doClearReplanning(iv.id)}
                              title="Conferma nuova data"
                              className="text-emerald-700 hover:bg-emerald-50 rounded p-1"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        {iv.needs_replanning && (
                          <div className="text-[10px] text-red-700 font-bold mt-0.5 uppercase tracking-wide">DA PIANIFICARE</div>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <Select value={slot} onValueChange={doSlot}>
                          <SelectTrigger data-testid={`slot-select-${iv.id}`} className="h-7 text-[11px] bg-white w-[130px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="full">Intera (09:00-18:00)</SelectItem>
                            <SelectItem value="morning">Mattina (09:00-13:30)</SelectItem>
                            <SelectItem value="afternoon">Pomeriggio (14:30-18:00)</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex flex-col gap-1">
                          <Input
                            data-testid={`start-input-${iv.id}`}
                            type="time"
                            defaultValue={iv.start_time || ""}
                            onBlur={(e) => { const v = e.target.value || ""; if (v !== (iv.start_time || "")) doField("start_time", v); }}
                            className="h-6 text-[11px] w-[82px] px-1"
                          />
                          <Input
                            data-testid={`end-input-${iv.id}`}
                            type="time"
                            defaultValue={iv.end_time || ""}
                            onBlur={(e) => { const v = e.target.value || ""; if (v !== (iv.end_time || "")) doField("end_time", v); }}
                            className="h-6 text-[11px] w-[82px] px-1"
                          />
                        </div>
                      </td>
                      <td className="py-2 pr-3 font-medium max-w-[160px]">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{cli?.name || "—"}</span>
                          <NoteCell iv={iv} onSaved={load} />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-slate-600 truncate max-w-[180px]" title={c?.title || ""}>{c?.title || "—"}</td>
                      <td className="py-2 pr-4">
                        {iv.status === "accepted" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-500 text-white rounded-md font-medium uppercase tracking-wide">
                            <CheckCircle2 className="w-3 h-3" /> Accettato
                          </span>
                        ) : confirmed ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md font-medium uppercase tracking-wide">
                            <CheckCircle2 className="w-3 h-3" /> Confermato
                          </span>
                        ) : (
                          <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md font-medium uppercase tracking-wide">Pianificato</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-1 items-center">
                          {confirmed ? (
                            <Button data-testid={`unconfirm-${iv.id}`} variant="ghost" size="sm" onClick={doUnconfirm} className="h-7 text-[11px] px-2">
                              Annulla
                            </Button>
                          ) : (
                            <Button
                              data-testid={`confirm-${iv.id}`}
                              variant="outline"
                              size="sm"
                              onClick={doConfirm}
                              disabled={!cli?.email}
                              title={cli?.email ? "Invia email di conferma al cliente" : "Cliente senza email in anagrafica — impossibile inviare conferma"}
                              className="h-7 text-[11px] px-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Mail className="w-3 h-3 mr-1" /> Conferma
                            </Button>
                          )}
                          <Button data-testid={`duplicate-intervention-${iv.id}`} variant="ghost" size="sm" onClick={() => doDuplicate(iv.id)} className="h-7 text-[11px] px-2 text-blue-700 hover:bg-blue-50 hover:text-blue-800" title="Duplica intervento al prossimo giorno lavorativo libero">
                            <Copy className="w-3.5 h-3.5 mr-1" /> Duplica
                          </Button>
                          <Button data-testid={`delete-intervention-${iv.id}`} variant="outline" size="sm" onClick={() => doDelete(iv.id)} className="h-7 text-[11px] px-2 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800" title="Cancella intervento (rimosso anche da Google Calendar)">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </Card>
      )}

      {/* Add intervention dialog (from Contratti da pianificare) */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-intervention-dialog" className="bg-white sm:max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo appuntamento</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded p-2">
              {(() => {
                const c = contractFor(addForm.contract_id);
                const cli = c ? clientFor(c.client_id) : null;
                return c ? (<><b>{cli?.name || "—"}</b> · {c.title}</>) : "";
              })()}
            </div>
            <div>
              <Label>Data *</Label>
              <Input data-testid="add-date-input" type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} />
            </div>
            <div>
              <Label>Fascia oraria</Label>
              <Select value={addForm.slot} onValueChange={(v) => setAddForm({ ...addForm, slot: v })}>
                <SelectTrigger data-testid="add-slot-select" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="full">Giornata intera (09:00 - 18:00)</SelectItem>
                  <SelectItem value="morning">Mattina (09:00 - 13:30)</SelectItem>
                  <SelectItem value="afternoon">Pomeriggio (14:30 - 18:00)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Orario personalizzato (opzionale)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="add-start-input" type="time" value={addForm.start_time} onChange={(e) => setAddForm({ ...addForm, start_time: e.target.value })} placeholder="Inizio" />
                <Input data-testid="add-end-input" type="time" value={addForm.end_time} onChange={(e) => setAddForm({ ...addForm, end_time: e.target.value })} placeholder="Fine" />
              </div>
            </div>
            <div>
              <Label>Note (opzionale)</Label>
              <Textarea
                data-testid="add-notes-input"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="Dettagli, riferimenti, materiali da portare..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="add-cancel" variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
            <Button data-testid="add-save" onClick={submitAdd} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Crea appuntamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
